/**
 * Module de recherche par véhicule.
 *
 * @export
 * @class RechercheParVehicule
 */
export class RechercheParVehicule {
    titre;
    icone;
    isOuvert = false; // Recherche fermée par défaut (mobile first)
    form;
    year;
    make;
    model;
    submodel;
    motorization;
    /**
     * Créer une instance de RechercheParVehicule.
     * @memberof RechercheParVehicule
     */
    constructor(formId) {
        // Mettre la main sur le formulaire et les dropdowns de la recherche
        this.titre = document.getElementById('titre-recherche-par-vehicule');
        this.icone = document.getElementById('icone-recherche-par-vehicule');
        this.form = document.getElementById(formId);
        this.year = this.form.querySelector('select[name="year"]');
        this.make = this.form.querySelector('select[name="make"]');
        this.model = this.form.querySelector('select[name="model"]');
        this.submodel = this.form.querySelector('select[name="vehicleIds"]');
        this.motorization = this.form.querySelector('select[name="vehicleId"]');
        // Ajouter les listeners
        this.ajouterListeners();
    }
    /**
     * Ajouter les listeners sur les champs select de la recherche.
     *
     * @private
     * @memberof RechercheParVehicule
     */
    ajouterListeners() {
        // Animer le module dans toutes les résolutions, sauf desktop
        const ecran = getComputedStyle(this.form).getPropertyValue('--ecran');
        if (ecran !== '"desktop"') {
            this.titre.addEventListener("click", this.toggle.bind(this));
        }
        // On bind 'this' pour passer l'objet à la fonction, sinon c'est compliqué
        this.year.addEventListener("change", this.changerYear.bind(this));
        this.make.addEventListener("change", this.changerMake.bind(this));
        this.model.addEventListener("change", this.changerModel.bind(this));
        this.submodel.addEventListener("change", this.changerSubmodel.bind(this));
        this.form.addEventListener("submit", (ev) => {
            ev.preventDefault();
            this.gererSoumissionFormulaire();
        });
    }
    /**
     * Ouvrir — ou fermer — l'élément
     *
     * @public
     * @memberof RechercheParVehicule
     */
    toggle() {
        if (this.isOuvert) {
            this.fermer();
        }
        else {
            this.ouvrir();
        }
    }
    /**
     * Afficher le formulaire
     *
     * @private
     * @memberof RechercheParVehicule
     */
    ouvrir() {
        // Afficher menu
        this.form.style.display = 'flex';
        // Modifier l'icône
        this.icone.style.transform = 'rotate(180deg)';
        // Modifier le statut
        this.isOuvert = true;
    }
    /**
     * Cacher le formulaire
     *
     * @private
     * @memberof RechercheParVehicule
     */
    fermer() {
        // Cacher menu en revenant aux css original
        this.form.style.display = '';
        // Modifier l'icône
        this.icone.style.transform = 'rotate(0)';
        // Modifier le statut
        this.isOuvert = false;
    }
    /**
     * Gérer un changement d'année.
     *
     * @private
     * @memberof RechercheParVehicule
     */
    changerYear() {
        // Réinitialiser tous les dropdowns
        this.make.options.length = 1;
        this.model.options.length = 1;
        this.submodel.options.length = 1;
        this.motorization.options.length = 1;
        // Récupérer les make en fonction de l'année
        fetch(`/en/vehicules/makes.json?year=${this.year.value}`, {
            headers: { "X-Requested-With": "XMLHttpRequest" },
        })
            .then((response) => response.json())
            .then((response) => {
            // Remplir le dropdown des makes
            for (const make in response) {
                if (response.hasOwnProperty(make)) {
                    const libelle = response[make];
                    this.make.appendChild(new Option(libelle, make));
                }
            }
        });
    }
    /**
     * Gérer un changement de manufacturier.
     *
     * @private
     * @memberof RechercheParVehicule
     */
    changerMake() {
        // Réinitialiser les dropdowns
        this.model.options.length = 1;
        this.submodel.options.length = 1;
        this.motorization.options.length = 1;
        // Récupérer la liste des models pour l'année et le make sélectionnés
        fetch(`/en/vehicules/models.json?year=${this.year.value}&make=${this.make.value}`, {
            headers: { "X-Requested-With": "XMLHttpRequest" },
        })
            .then((response) => response.json())
            .then((response) => {
            // Remplir le dropdown des models
            for (const model in response) {
                if (response.hasOwnProperty(model)) {
                    const libelle = response[model];
                    this.model.appendChild(new Option(libelle, model));
                }
            }
        });
    }
    /**
     * Gérer un changement de modèle.
     *
     * @private
     * @memberof RechercheParVehicule
     */
    changerModel() {
        // Réinitialiser les dropdowns
        this.submodel.options.length = 1;
        this.motorization.options.length = 1;
        // Récupérer la liste des sous-models pour l'année, le make et le model sélectionnés
        fetch(`/en/vehicules/submodels.json?year=${this.year.value}&make=${this.make.value}&model=${encodeURIComponent(this.model.value)}`, {
            headers: { "X-Requested-With": "XMLHttpRequest" },
        })
            .then((response) => response.json())
            .then((response) => {
            // Remplir le dropdown des sous-modèles
            for (const submodel in response) {
                if (response.hasOwnProperty(submodel)) {
                    const libelle = response[submodel];
                    this.submodel.appendChild(new Option(`${libelle.submodel} ${libelle.engtype} ${libelle.liter}L`, libelle.vehiculeIds));
                }
            }
        });
    }
    /**
     * Gérer un changement de sous-modèle.
     *
     * @private
     * @memberof RechercheParVehicule
     */
    changerSubmodel() {
        // Réinitialiser le dropdown de motorisation
        this.motorization.options.length = 1;
        // Mettre la main sur le ou les véhicules ids trouvés jusqu'à date
        const vehicleIds = this.submodel.value;
        console.log(vehicleIds);
        // Cacher le champ motorisation s'il y a un seul véhicule qui correspond à la recherche
        if (!vehicleIds.includes(',')) {
            this.motorization.required = false;
            this.motorization.classList.add('form__dropdown--cacher');
            // Pas besoin d'aller plus loin
            return;
        }
        // Récupérer la liste des motorisations s'il y a plusieurs véhicules ids
        this.motorization.required = true;
        this.motorization.classList.remove('form__dropdown--cacher');
        fetch(`/en/vehicules/motorizations.json?vehicleIds=${vehicleIds}`, {
            headers: { "X-Requested-With": "XMLHttpRequest" },
        })
            .then((response) => response.json())
            .then((response) => {
            // Remplir le dropdown de motorisations
            for (const motorization in response) {
                if (response.hasOwnProperty(motorization)) {
                    const libelle = response[motorization];
                    this.motorization.appendChild(new Option(`${libelle.fuel} ${libelle.fueldel} ${libelle.asp} ${libelle.engvin} ${libelle.engdesg}`, libelle.vehicule_id));
                }
            }
        });
    }
    /**
     * Gérer la soumission du formulaire de recherche par véhicule.
     *
     * @private
     * @return {*}  {void}
     * @memberof RechercheParVehicule
     */
    gererSoumissionFormulaire() {
        const vehicleId = this.motorization.value || this.submodel.value;
        if (!vehicleId || vehicleId.includes(',')) { // Un ou plusieurs champ(s) vide(s).
            const errorMessage = this.form.dataset.errorMessage;
            Array.from(this.form.elements).forEach((element) => {
                if (!(element instanceof HTMLSelectElement))
                    return;
                element.setCustomValidity(''); // Nettoyer entre les tentatives de soumission.
                if (!element.value) {
                    element.setCustomValidity(errorMessage);
                }
            });
            this.form.reportValidity();
            return;
        }
        window.open(this.form.action.replace('%7BvehicleId%7D', vehicleId));
    }
}
